import { cn } from '@/lib/utils';
import { FBLive } from '@/types/fb-type';
import { Heart, MessageCircle, Video } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  effectiveLiveStatus,
  isCurrentlyLiveStatus,
  parseLiveTimeMs,
} from '@/pages/sale/lib/live-status';

interface LiveCardProps {
  live: FBLive;
  isSelected: boolean;
  onSelect: (live: FBLive) => void;
  priority?: boolean;
}

function formatLiveTime(iso: string | null | undefined) {
  if (!iso) return 'Unknown time';
  const ms = parseLiveTimeMs(iso);
  if (Number.isNaN(ms)) return 'Unknown time';
  const date = new Date(ms);

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  if (sameDay) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCount(value: number | null | undefined) {
  return (value ?? 0).toLocaleString();
}

function LiveStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const isLive = isCurrentlyLiveStatus(status);

  if (isLive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200/80 bg-red-600/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Live
      </span>
    );
  }

  if (normalized === 'scheduled') {
    return (
      <span className="rounded-full border border-amber-200/80 bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
        Scheduled
      </span>
    );
  }

  if (normalized === 'vod' || normalized === 'completed') {
    return (
      <span className="rounded-full border border-slate-200/80 bg-slate-900/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
        Replay
      </span>
    );
  }

  return (
    <span className="rounded-full border border-slate-200/80 bg-slate-900/80 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
      {status}
    </span>
  );
}

function LivePreviewFallback({ status }: { status: string }) {
  const isLive = isCurrentlyLiveStatus(status);

  return (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center',
        isLive
          ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-red-950'
          : 'bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300',
      )}
      aria-hidden
    >
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full',
          isLive ? 'bg-white/10 text-red-200' : 'bg-white/70 text-slate-500',
        )}
      >
        <Video className="h-4 w-4" strokeWidth={1.75} />
      </div>
    </div>
  );
}

function LivePreview({
  previewUrl,
  title,
  priority,
  onError,
}: {
  previewUrl: string;
  title: string;
  priority: boolean;
  onError: () => void;
}) {
  return (
    <img
      src={previewUrl}
      alt={title}
      width={112}
      height={63}
      className="h-full w-full object-cover"
      loading={priority ? 'eager' : 'lazy'}
      {...(priority ? { fetchpriority: 'high' as const } : { fetchpriority: 'auto' as const })}
      decoding="async"
      onError={onError}
    />
  );
}

export default function LiveCard({ live, isSelected, onSelect, priority = false }: LiveCardProps) {
  const title = live.title?.trim() || 'Untitled live';
  const description = live.description?.trim() || '';
  const createdAtLabel = formatLiveTime(live.creationTime);
  const viewsLabel = live.liveViews != null ? formatCount(live.liveViews) : null;
  const previewUrl = live.previewUrl?.trim() || null;
  const [previewFailed, setPreviewFailed] = useState(false);
  const displayStatus = effectiveLiveStatus(live);
  const isLiveNow = isCurrentlyLiveStatus(displayStatus);

  useEffect(() => {
    setPreviewFailed(false);
  }, [previewUrl]);

  const showPreviewFallback = !previewUrl || previewFailed;

  return (
    <button
      type="button"
      onClick={() => onSelect(live)}
      className={cn(
        'w-full rounded-lg border bg-white text-left transition-colors',
        'hover:border-slate-300 hover:bg-slate-50/50',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40',
        isSelected
          ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-500/20'
          : 'border-slate-200',
      )}
      aria-pressed={isSelected}
    >
      <div className="flex gap-2.5 p-2">
        <div className="relative aspect-video w-[112px] shrink-0 overflow-hidden rounded-md border border-slate-100 bg-slate-100">
          {showPreviewFallback ? (
            <LivePreviewFallback status={displayStatus} />
          ) : (
            <LivePreview
              previewUrl={previewUrl}
              title={title}
              priority={priority}
              onError={() => setPreviewFailed(true)}
            />
          )}

          <div className="absolute left-1 top-1">
            <LiveStatusBadge status={displayStatus} />
          </div>

          {viewsLabel && isLiveNow && (
            <div className="absolute bottom-1 left-1 rounded bg-black/65 px-1 py-0.5 text-[9px] font-medium tabular-nums text-white">
              {viewsLabel} watching
            </div>
          )}

          {isSelected && (
            <div className="absolute inset-0 ring-2 ring-inset ring-blue-500/70" aria-hidden />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold leading-tight text-slate-900">{title}</p>
            {description ? (
              <p className="mt-0.5 line-clamp-1 text-[11px] leading-snug text-slate-500">
                {description}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
            <time className="truncate tabular-nums" dateTime={live.creationTime}>
              {createdAtLabel}
            </time>

            <div className="flex shrink-0 items-center gap-2 tabular-nums">
              <span className="inline-flex items-center gap-0.5" title="Comments">
                <MessageCircle className="h-3 w-3 text-slate-400" />
                {formatCount(live.commentCount)}
              </span>
              <span className="inline-flex items-center gap-0.5" title="Reactions">
                <Heart className="h-3 w-3 text-slate-400" />
                {formatCount(live.reactionCount)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
